import {
	clearEmployeeFnf,
	getFnfClearanceList,
} from 'Api/fnfClearance';
import TableActionHeader from 'components/Global/TableActionHeader';
import useDebounce from 'components/Global/Hooks/UseDebounce';
import { useCallback, useEffect, useState } from 'react';
import { toastText } from 'utils/utils';
import ClearFnfModal from './ClearFnfModal';
import ViewFnfDetailsModal from './ViewFnfDetailsModal';
import FnfClearanceHeader from './FnfClearanceHeader';
import FnfClearanceTable from './FnfClearanceTable';
import styles from './index.module.scss';

const FnfClearanceComponent = () => {
	const [searchValue, setSearchValue] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [totalRecords, setTotalRecords] = useState(0);
	const [sortField, setSortField] = useState('employeeName');
	const [sortOrder, setSortOrder] = useState('asc');
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [listData, setListData] = useState<any[]>([]);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isViewModalOpen, setIsViewModalOpen] = useState(false);
	const [selectedEmployee, setSelectedEmployee] = useState<any>(null);

	const debouncedSearchValue = useDebounce(searchValue, 500);

	const fetchList = useCallback(async () => {
		try {
			setIsLoading(true);
			const apiRes = await getFnfClearanceList({
				page: currentPage,
				pageSize,
				search: debouncedSearchValue,
				sortBy: sortField,
				sortOrder,
			});
			setListData(apiRes.data.data ?? []);
			setTotalRecords(apiRes.data.total ?? 0);
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong while fetching FnF clearance list';
			toastText(errorMessage, 'error');
		} finally {
			setIsLoading(false);
		}
	}, [
		currentPage,
		pageSize,
		debouncedSearchValue,
		sortField,
		sortOrder,
	]);

	useEffect(() => {
		fetchList();
	}, [fetchList]);

	const handleSearch = (value: string) => {
		setSearchValue(value);
		setCurrentPage(1);
	};

	const handleSearchApi = () => {};

	const tableChangeHandler = (
		pagination: any,
		_filters: any,
		sorter: any
	) => {
		setTotalRecords(pagination.total);
		setCurrentPage(pagination.current);
		setPageSize(pagination.pageSize);
		let order = '';
		if (sorter?.order === 'ascend') {
			order = 'asc';
		} else if (sorter?.order) {
			order = 'desc';
		}
		setSortOrder(order || 'asc');
		setSortField(String(sorter.field ?? 'employeeName'));
	};

	const onClearClick = (record: any) => {
		setSelectedEmployee(record);
		setIsModalOpen(true);
	};

	const onViewDetailsClick = (record: any) => {
		setSelectedEmployee(record);
		setIsViewModalOpen(true);
	};

	const handleViewModalCancel = () => {
		setIsViewModalOpen(false);
		setSelectedEmployee(null);
	};

	const handleModalCancel = () => {
		setIsModalOpen(false);
		setSelectedEmployee(null);
	};

	const handleModalSubmit = async (values: {
		leaveDays: number;
		leaveAmount: number;
		gratuityAmount: number;
	}) => {
		if (!selectedEmployee) return;
		try {
			setIsSubmitting(true);
			await clearEmployeeFnf({
				employeeId: selectedEmployee.employeeId,
				contractId: selectedEmployee.contractId,
				leaveDays: values.leaveDays,
				leaveAmount: values.leaveAmount,
				gratuityAmount: values.gratuityAmount,
			});
			toastText('Leave and gratuity cleared successfully', 'success');
			handleModalCancel();
			fetchList();
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong while clearing FnF';
			toastText(errorMessage, 'error');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className={styles['employee-container']}>
			<TableActionHeader title="Employee FnF Clearance" />
			<FnfClearanceHeader
				searchValue={searchValue}
				handleSearch={handleSearch}
				handleSearchApi={handleSearchApi}
			/>
			<FnfClearanceTable
				data={listData}
				totalRecords={totalRecords}
				currentPage={currentPage}
				pageSize={pageSize}
				isLoading={isLoading}
				tableChangeHandler={tableChangeHandler}
				onClearClick={onClearClick}
				onViewDetailsClick={onViewDetailsClick}
			/>
			<ClearFnfModal
				isOpen={isModalOpen}
				isLoading={isSubmitting}
				selectedEmployee={selectedEmployee}
				onCancel={handleModalCancel}
				onSubmit={handleModalSubmit}
			/>
			<ViewFnfDetailsModal
				isOpen={isViewModalOpen}
				selectedEmployee={selectedEmployee}
				onCancel={handleViewModalCancel}
			/>
		</div>
	);
};

export default FnfClearanceComponent;
